import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  AccountTree as AccountTreeIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import apiClient from '../../services/api/client';
import { TopicFrameworkDisplay } from './TopicFrameworkDisplay';
import { TopicFramework } from './types';

interface JsonPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  filePath: string;
  fileName: string;
  clientId?: string;
}

const JsonPreviewDialog: React.FC<JsonPreviewDialogProps> = ({
  open,
  onClose,
  filePath,
  fileName,
  clientId
}) => {
  const [jsonContent, setJsonContent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');
  const [isTopicFramework, setIsTopicFramework] = useState(false);

  // Detect if JSON is a topic framework
  const detectTopicFramework = (data: any): boolean => {
    if (!data || typeof data !== 'object') return false;
    
    // Check for topic framework structure
    return (
      data.topics && 
      (Array.isArray(data.topics) || typeof data.topics === 'object') &&
      (data.version !== undefined || data.created_at !== undefined)
    );
  };

  // Fetch JSON content when dialog opens
  useEffect(() => {
    if (open && filePath) {
      fetchJsonContent();
    }
  }, [open, filePath]);

  // Function to fetch JSON content
  const fetchJsonContent = async () => {
    if (!filePath || !clientId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/storage/get', {
        params: {
          path: filePath,
          container: 'client-data',
          client_id: clientId
        }
      });

      // Parse JSON content
      let parsedData;
      if (typeof response.data === 'string') {
        try {
          parsedData = JSON.parse(response.data);
          setJsonContent(parsedData);
        } catch (parseErr) {
          console.error('Error parsing JSON:', parseErr);
          setError('The file does not contain valid JSON data');
          setJsonContent(response.data);
          return;
        }
      } else {
        // Already parsed
        parsedData = response.data;
        setJsonContent(parsedData);
      }

      // Check if this is a topic framework
      const isFramework = detectTopicFramework(parsedData);
      setIsTopicFramework(isFramework);
      
      // Default to structured view for topic frameworks
      if (isFramework) {
        setViewMode('structured');
      }
    } catch (err) {
      console.error('Error fetching JSON content:', err);
      setError('Failed to load JSON content');
    } finally {
      setLoading(false);
    }
  };

  // Copy JSON to clipboard
  const handleCopyJson = () => {
    if (!jsonContent) return;

    const jsonString = JSON.stringify(jsonContent, null, 2);
    navigator.clipboard.writeText(jsonString)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy JSON:', err);
        setError('Failed to copy to clipboard');
      });
  };

  // Reset copied state after timeout
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (copied) {
      timeoutId = setTimeout(() => {
        setCopied(false);
      }, 3000); // Reset after 3 seconds
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [copied]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="json-preview-dialog-title"
    >
      <DialogTitle id="json-preview-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            {fileName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isTopicFramework && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="structured" aria-label="structured view">
                  <AccountTreeIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Structured
                  </Typography>
                </ToggleButton>
                <ToggleButton value="raw" aria-label="raw JSON">
                  <CodeIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Raw JSON
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleCopyJson}
              disabled={!jsonContent || loading}
              startIcon={copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
              color={copied ? "success" : "primary"}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="close"
              sx={{ ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : jsonContent ? (
          <>
            {isTopicFramework && viewMode === 'structured' ? (
              <Box sx={{ p: 1 }}>
                <TopicFrameworkDisplay topicFramework={jsonContent as TopicFramework} />
              </Box>
            ) : (
              <Box sx={{ 
                bgcolor: 'background.paper',
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                  {JSON.stringify(jsonContent, null, 2)}
                </pre>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No content to display
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JsonPreviewDialog;
